import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "hooks/useApi";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useNotification } from "./useNotification";

export const useFetchFixturesByTaskStatus = ({
  seasonId,
  taskStatus,
  page,
  pageSize,
}: {
  seasonId?: string;
  taskStatus?: any;
  page?: any;
  pageSize: number;
}) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  const paramsObj: any = {
    taskStatus,
    limit: pageSize,
    offset: pageSize * page - pageSize,
  };

  if (seasonId) {
    paramsObj.seasonId = seasonId;
  }

  const params = new URLSearchParams(paramsObj);

  return useQuery({
    queryKey: ["fixtures", sport, organizationId, params.toString()],
    queryFn: () => callApi(`/${sport}/o/${organizationId}/fixtures?${params.toString()}`),
    placeholderData: (prev) => prev,
  });
};

export const useFetchFixture = ({
  entityId,
  seasonId,
  fixtureId,
}: {
  entityId: string;
  seasonId: string;
  fixtureId: string;
}) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  return useQuery({
    queryKey: ["fixture", sport, organizationId, entityId, seasonId, fixtureId],
    queryFn: () =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}`, {
        defaultErrorMessage: "Can't fetch fixture",
      }),
  });
};

export const useFetchFixtures = ({
  entityId,
  seasonId,
  enabled,
}: {
  entityId: string;
  seasonId: string;
  enabled?: boolean;
}) => {
  const callApi = useApi();
  const { sport, organizationId } = useParams();

  return useQuery({
    queryKey: ["fixtures", sport, organizationId, entityId, seasonId],
    queryFn: () =>
      callApi(`/${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures`, {
        defaultErrorMessage: "Can't fetch fixtures",
      }),
    enabled,
  });
};

export const useUpdateFixtureEntityUniform = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const { formatMessage } = useIntl();
  const callApi = useApi();
  const { showSuccessNotification } = useNotification();

  return useMutation({
    mutationFn: ({
      entityId,
      seasonId,
      fixtureId,
      uniformId,
    }: {
      entityId: string;
      seasonId: string;
      fixtureId: string;
      uniformId: string | null;
    }) => {
      const url = `${sport}/o/${organizationId}/entities/${entityId}/seasons/${seasonId}/fixtures/${fixtureId}/uniforms`;
      return callApi(`/${url}`, {
        method: "PATCH",
        body: {
          uniformId,
        },
      });
    },
    onSuccess: async (
      _: any,
      {
        entityId,
        seasonId,
        fixtureId,
      }: {
        entityId: string;
        seasonId: string;
        fixtureId: string;
        uniformId: string | null;
      }
    ) => {
      await queryClient.invalidateQueries({
        queryKey: ["fixtures", sport, organizationId, entityId, seasonId, fixtureId],
      });
      showSuccessNotification(formatMessage({ id: "notification.uniform.selected" }));
    },
  });
};
